import { fetchAllPvSize } from '../../services/allPvSizeService';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { isSolarApprovalCohortYear } from '../../services/solarAnalysisCutoff';

interface SolarPVCapacityData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  totalCapacity?: number;
  latestYearCapacity?: number;
  latestYear?: number | string;
  percentChange?: number;
  percentChangeSince2015?: number;
  sectors?: string[];
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVCapacitySupabase(location: string): Promise<SolarPVCapacityData> {
  try {

    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Capacity by Sector - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Capacity (MW DC)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        totalCapacity: 0,
        latestYearCapacity: 0,
        sectors: []
      };
    }

    // Query Supabase by geoid for server-side filtering
    const { data: pvData, error } = await fetchAllPvSize({
      select: '*',
      geofips
    });

    if (error) {
      console.error('Error fetching Solar PV Capacity data:', error);
      throw error;
    }


    if (!pvData || pvData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Capacity by Sector - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Capacity (MW DC)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV capacity data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        totalCapacity: 0,
        latestYearCapacity: 0,
        sectors: []
      };
    }

    // Process the data to create chart datasets (like the HTML version)
    const allYears = Array.from(new Set(pvData.map(item => parseInt(item.year)))).sort();
    const years = allYears.filter((year) => isSolarApprovalCohortYear(year));
    const sectors = Array.from(new Set(pvData.map(item => item.sector))).filter(Boolean);
    
    const datasets = sectors.map((sector, index) => {
      const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#06b6d4'];
      const color = colors[index % colors.length];
      
      const data = years.map(year => {
        const yearData = pvData.filter(item => 
          parseInt(item.year) === year && item.sector === sector
        );
        return yearData.reduce((sum, item) => sum + (parseFloat(item.total_watts) || 0), 0) / 1000000; // Convert W to MW
      });

      return {
        label: sector,
        data: data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        fill: true,
        tension: 0.1
      };
    });

    const chartData = {
      labels: years,
      datasets: datasets
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar PV Capacity by Sector - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Capacity (MW DC)'
          },
          beginAtZero: true
        }
      }
    };

    const inScope = pvData.filter((item) => isSolarApprovalCohortYear(parseInt(String(item.year), 10)));
    const totalCapacityMw =
      inScope.reduce((sum, item) => sum + (parseFloat(item.total_watts) || 0), 0) / 1_000_000;
    const latestYear = years.length ? Math.max(...years) : null;
    const latestYearCapacityMw =
      latestYear != null
        ? inScope
            .filter((item) => parseInt(String(item.year), 10) === latestYear)
            .reduce((sum, item) => sum + (parseFloat(item.total_watts) || 0), 0) / 1_000_000
        : 0;

    return {
      chartData,
      chartOptions,
      totalCapacity: totalCapacityMw,
      latestYearCapacity: latestYearCapacityMw,
      latestYear: latestYear != null ? latestYear.toString() : undefined,
      sectors,
    };

  } catch (error) {
    console.error('Error in fetchSolarPVCapacitySupabase:', error);
    throw error;
  }
}
